import { motion } from 'framer-motion'
import { formatConfidence, getConfidenceLevel } from '@/utils/helpers'

export interface ConfidenceHeatmapProps {
  confidence: number[][]
  compact?: boolean
}

const ConfidenceHeatmap = ({ confidence, compact = false }: ConfidenceHeatmapProps) => {
  const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h']
  const ranks = ['8', '7', '6', '5', '4', '3', '2', '1']

  const getHeatmapColor = (value: number) => {
    if (value > 0.95) return 'bg-green-500'
    if (value > 0.85) return 'bg-yellow-500'
    if (value > 0.7) return 'bg-orange-500'
    return 'bg-red-500'
  }

  const gridGapClass = compact ? 'gap-0.5' : 'gap-1'
  const containerClass = compact ? 'max-w-sm mx-auto' : 'max-w-lg'
  const squareLabelClass = compact ? 'text-[9px]' : 'text-[10px]'
  const valueLabelClass = compact ? 'text-[10px]' : 'text-xs'

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.3 }}
      className={`w-full ${containerClass}`}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-600 dark:text-dark-300 uppercase tracking-wider">
          Confidence Heatmap
        </h3>
      </div>

      <div className="bg-white dark:bg-dark-800 p-2 rounded-lg border border-gray-200 dark:border-dark-700">
        <div className={`grid grid-cols-8 ${gridGapClass}`}>
          {confidence.map((row, rowIndex) =>
            row.map((value, colIndex) => {
              const square = `${files[colIndex]}${ranks[rowIndex]}`
              const { label } = getConfidenceLevel(value)

              return (
                <motion.div
                  key={square}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    duration: 0.2,
                    delay: (rowIndex * 8 + colIndex) * 0.01,
                  }}
                  className={`
                    aspect-square rounded-sm flex flex-col items-center justify-center
                    cursor-help transition-transform hover:scale-110 hover:z-10
                    ${getHeatmapColor(value)}
                  `}
                  title={`${square}: ${formatConfidence(value)} (${label})`}
                >
                  <span className={`${squareLabelClass} font-mono text-white/80 leading-none`}>
                    {square}
                  </span>
                  <span className={`${valueLabelClass} font-bold text-white leading-none mt-0.5`}>
                    {formatConfidence(value)}
                  </span>
                </motion.div>
              )
            })
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-3 justify-center text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-sm bg-green-500" />
          <span className="text-gray-600 dark:text-dark-400">High (&gt;95%)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-sm bg-yellow-500" />
          <span className="text-gray-600 dark:text-dark-400">Medium (85-95%)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-sm bg-orange-500" />
          <span className="text-gray-600 dark:text-dark-400">Low (70-85%)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-sm bg-red-500" />
          <span className="text-gray-600 dark:text-dark-400">Very Low (&lt;70%)</span>
        </div>
      </div>
    </motion.div>
  )
}

export default ConfidenceHeatmap
